import { useEffect } from 'react';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import DependencyFlowHelper from '../../../helper/DependencyFlowHelper';
import { DependencyFlowStatus } from '../../../slices/DataSourceControllerSlice/types';
import { getIsDataSourceAnEntityType } from '../../../utils/entityDataSource';
import {
  useDSCActions,
  useDSCDataSourceRecordStore,
  useDSCEntityTypeDataSources,
  useDSCGlobalStateStore,
} from '../context/selectors';
import { useGetDataSourceRecord } from './useGetDataSourceRecord';
import { useIsDataSourceDisabled } from './useIsDataSourceDisabled';
import useShowDisabledDataSourceSnackbar from './useShowDisabledDataSourceSnackbar';

function useFinishDisabledAutomaticDS(automaticDataSourceIds: string[]) {
  const { getIsDisabled } = useIsDataSourceDisabled();
  const entityTypeDataSources = useDSCEntityTypeDataSources();
  const dataSourceRecords = useDSCDataSourceRecordStore().hooks.useDataSources();
  const dataSourceDependencyFlow = useDSCGlobalStateStore().hooks.dataSource.useDependencyFlow();
  const { addDependencyFlows } = useDSCActions();
  const { getDataSourceRecord } = useGetDataSourceRecord();
  const { showDisabledDSMessage } = useShowDisabledDataSourceSnackbar(getDataSourceRecord);

  const disabledDataSources = automaticDataSourceIds.filter((dataSourceId) => {
    const record = getIsDataSourceAnEntityType(dataSourceId)
      ? entityTypeDataSources?.[dataSourceId]
      : dataSourceRecords[dataSourceId];

    const { queryDisabled, queryEnabled } = DataSourceHelper.advancedOptions(record);

    // disabled if queryDisabled is truthy, or queryEnabled is set and evaluates explicitly false
    if (getIsDisabled({ disabled: queryDisabled })) return true;
    if (!queryEnabled) return false;
    return getIsDisabled({ disabled: queryEnabled }) === false;
  });

  const nonFinishedDisabledDataSources = disabledDataSources.filter((dataSourceId) => {
    return dataSourceDependencyFlow[dataSourceId]?.status !== DependencyFlowStatus.Finished;
  });

  useEffect(() => {
    // if the ds was made disabled due to some run time condition, but it is not yet finished,
    // that means some parent might be waiting for this ds to finish, so it can go to Commit stage, that's why move
    // it by ourselves
    if (nonFinishedDisabledDataSources.length) {
      addDependencyFlows(
        DependencyFlowHelper.createFinishedDependencyFlow({
          ids: nonFinishedDisabledDataSources,
        }),
      );

      nonFinishedDisabledDataSources.forEach((dataSourceId) => {
        showDisabledDSMessage(dataSourceId);
      });
    }
  }, [addDependencyFlows, nonFinishedDisabledDataSources, showDisabledDSMessage]);

  return {
    disabledDataSources: disabledDataSources.reduce<Record<string, boolean>>((acc, id) => {
      acc[id] = true;
      return acc;
    }, {}),
  };
}

export default useFinishDisabledAutomaticDS;
